import React from 'react';
import { useSetAtom } from 'jotai';

import ErrorDialog from '@smart-tv/components/error-dialog/error-dialog';

import useFavoritedContent from '@smart-tv/hooks/use-favorited-content';
import { ContentItem, ContentType } from '@smart-tv/typings/content';
import { rootStore as stripesRootStore } from '@smart-tv/components/stripes/_stores/store-root';
import { rootStore as programSheetRootStore } from '@smart-tv/components/program-sheet/_stores/store-root';
import { focusKeyPrevAtom } from '@smart-tv/atoms';

interface Props {
  contentItem?: ContentItem;
  showUnavailableDialog: boolean;
  setShowUnavailableDialog: (show: boolean) => void;
  focusKey?: string;
  handleFavoritesUpdate?: (item: ContentItem, action: 'add' | 'remove') => void;
}

export const ContentSliderItemUnavailableDialog: React.FC<Props> = (props: Props) => {
  const { contentItem, showUnavailableDialog, setShowUnavailableDialog, handleFavoritesUpdate } =
    props;
  const { unfavoriteContent } = useFavoritedContent();
  const setFocusKeyPrev = useSetAtom(focusKeyPrevAtom);

  const mapContentTypeToEnum = React.useCallback(
    async (contentType: string): Promise<ContentType.Movies | ContentType.Series | undefined> => {
      switch (contentType) {
        case 'MOVIES':
          return ContentType.Movies;
        case 'SERIES':
          return ContentType.Series;
        default:
          return undefined;
      }
    },
    []
  );

  const handleRemoveFromFavorites = React.useCallback(async () => {
    if (contentItem?.contentId && contentItem?.contentType && contentItem?.title) {
      try {
        const contentTypeEnum = await mapContentTypeToEnum(contentItem.contentType);
        if (contentTypeEnum) {
          await unfavoriteContent({
            id: contentItem.contentId,
            type: contentTypeEnum,
            title: contentItem.title
          });

          // Update the stripe immediately by calling the favorites callback
          if (handleFavoritesUpdate && contentItem) {
            handleFavoritesUpdate(contentItem, 'remove');
          }

          // Update sheet store if the current sheet matches the item being unfavorited
          if (programSheetRootStore.sheetStore.sheet?.contentId === contentItem.contentId) {
            programSheetRootStore.sheetStore.setIsCustomerFavorite(false);
          }

          // Clear focusKeyPrev to prevent focus from being restored to an item that might be removed
          setFocusKeyPrev(null);

          // Trigger refetch for all stripes with update_on_favorite: true
          stripesRootStore.stripesStore.refetchUpdateOnFavoriteStripes();

          setShowUnavailableDialog(false);
        }
      } catch (error) {
        console.error('Failed to remove content from favorites:', error);
        // Leave dialog open on error so user can retry or dismiss (handleKmmError will handle showing the error dialog)
      }
    }
  }, [
    contentItem,
    unfavoriteContent,
    mapContentTypeToEnum,
    setShowUnavailableDialog,
    handleFavoritesUpdate,
    setFocusKeyPrev
  ]);

  if (!showUnavailableDialog || !contentItem?.title) {
    return null;
  }

  return (
    <ErrorDialog
      title={contentItem.title}
      message="er pt. ikke tilgængelig"
      secondaryButton={{
        label: 'Behold i Mine Favoritter',
        onClick: () => setShowUnavailableDialog(false)
      }}
      primaryButton={{
        label: 'Fjern fra Mine Favoritter',
        onClick: handleRemoveFromFavorites
      }}
    />
  );
};

export default ContentSliderItemUnavailableDialog;
